import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  FileText,
  ImagePlus,
  Loader2,
  Copy,
  Download,
  X,
  Sparkles,
  ScanText,
  FileDown,
  Save,
  FolderOpen,
  Trash2,
  Eye,
  GripVertical,
  HardDrive,
  ArrowUp,
  ArrowDown,
  Package,
} from "lucide-react";
import JSZip from "jszip";
import { toast } from "sonner";
import { fetchWithAuth } from "@/lib/fetch-with-auth";
import { generateLetterPdf } from "@/lib/api/letter.functions";
import {
  saveCvLibraryItem,
  listCvLibrary,
  deleteCvLibraryItem,
  fetchCvLibraryItem,
} from "@/lib/api/cv-library.functions";
import { supabase } from "@/integrations/supabase/client";
import { FeatureErrorBoundary } from "@/components/site/FeatureErrorBoundary";
import {
  SPECULATIVE_LEGAL_CONTACTS,
  SPECULATIVE_COMBINED_ROLE,
  buildSpeculativeBrief,
  validateSpeculativeLetter,
  type SpeculativeLegalContact,
  type VacancyMode,
  type SpeculativeLetterIssue,
} from "@/lib/speculative-legal-contacts";
import {
  checkCvLetterAlignment,
  type AlignmentIssue,
} from "@/lib/cv-letter-alignment";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Letter = {
  roleTitle: string;
  employer: string;
  recipient: string;
  date: string;
  salutation: string;
  body: string;
  sign_off: string;
  candidateName: string;
  candidateContact: string;
};

type Extracted = {
  roleTitle?: string;
  employer?: string;
  location?: string;
  recipient?: string;
  responsibilities?: string[];
  requirements?: string[];
  closingDate?: string;
  referenceNumber?: string;
};

export interface MotivationalLetterGeneratorProps {
  candidateInfo?: string | null;
  triggerLabel?: string;
}

/** Coerce anything (string / object / null / undefined) into a safe string. */
function coerceCandidateInfo(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  try {
    return typeof v === "object" ? JSON.stringify(v, null, 2) : String(v);
  } catch {
    return "";
  }
}

/** Default admin candidate profile (Nkadimeng Danny) — quick-fill for admin tab. */
const ADMIN_PROFILE = {
  candidateName: "Nkadimeng Danny",
  email: "nkadimengdanny7@gmail.com",
  phone: "0712766198",
} as const;

async function fileToBase64(file: File): Promise<{ mime: string; data: string }> {
  if (!(file instanceof File)) throw new Error("Invalid file input.");
  const buf = new Uint8Array(await file.arrayBuffer());
  let bin = "";
  for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]);
  return { mime: file.type || "image/jpeg", data: btoa(bin) };
}

export function MotivationalLetterGenerator(props: MotivationalLetterGeneratorProps = {}) {
  return (
    <FeatureErrorBoundary
      featureName="Motivational Letter Generator"
      context={{ candidateInfoType: typeof props?.candidateInfo, hasProps: Boolean(props) }}
    >
      <MotivationalLetterGeneratorInner {...(props ?? {})} />
    </FeatureErrorBoundary>
  );
}

function MotivationalLetterGeneratorInner(props: MotivationalLetterGeneratorProps = {}) {
  // Defensive destructuring — never assume `props` or its fields are defined.
  const candidateInfoProp = coerceCandidateInfo(props?.candidateInfo);
  const triggerLabel = props?.triggerLabel ?? "Motivational letter";


  const [open, setOpen] = useState(false);
  const [jobText, setJobText] = useState("");
  const [candidate, setCandidate] = useState(candidateInfoProp);
  const [tone, setTone] = useState("professional");
  const [preview, setPreview] = useState<string | null>(null);
  const [letter, setLetter] = useState<Letter | null>(null);
  const [extracted, setExtracted] = useState<Extracted | null>(null);
  const [appEmail, setAppEmail] = useState<{
    subject: string;
    greeting: string;
    body: string;
    sign_off: string;
    signature: string;
  } | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const imageFileRef = useRef<File | null>(null);
  const cvFileInputRef = useRef<HTMLInputElement | null>(null);
  const [cvFileMeta, setCvFileMeta] = useState<{ name: string; mime: string; size: number } | null>(null);
  const cvBase64Ref = useRef<{ mime: string; base64: string } | null>(null);
  const [cvExtracting, setCvExtracting] = useState(false);
  const supportInputRef = useRef<HTMLInputElement | null>(null);
  const [supportDocs, setSupportDocs] = useState<
    Array<{ name: string; mime: string; size: number; base64: string }>
  >([]);
  const [authedUserId, setAuthedUserId] = useState<string | null>(null);
  // Undo-window deletes: track ids the user asked to remove; hide from UI immediately
  // and only hit the server after ~6s if not undone.
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set());
  const deleteTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Drag-and-drop reordering for the current session's supporting documents.
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // In-app preview of a saved library item (PDF iframe or image).
  const [libPreview, setLibPreview] = useState<
    | { id: string; name: string; mime: string; url: string; kind: "cv" | "support" }
    | null
  >(null);
  const [libPreviewLoading, setLibPreviewLoading] = useState<string | null>(null);

  // Speculative / Apollo.io lead selection.
  const [specContactId, setSpecContactId] = useState<string>("");

  // Multi-role package: user can select several positions and get one ZIP
  // per role (letter PDF + tailored CV PDF + email .txt).
  const ROLE_OPTIONS = [
    "Articles of Clerkship",
    "Candidate Attorney",
    "Paralegal",
    "Legal Assistant",
    "Internship",
  ] as const;
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  // Vacancy focus for speculative letters: current openings, future
  // vacancies, or both (default). Rewords the letter but always addresses
  // the same specific person and firm in a single unified letter.
  const [vacancyMode, setVacancyMode] = useState<VacancyMode>("both");
  // Guardrail: user can override validation errors and download anyway.
  const [validationOverride, setValidationOverride] = useState(false);


  useEffect(() => {
    return () => {
      // Flush any pending deletes on unmount so the user's action isn't lost.
      deleteTimers.current.forEach((t) => clearTimeout(t));
      deleteTimers.current.clear();
      if (libPreview?.url) URL.revokeObjectURL(libPreview.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    supabase.auth.getUser().then(({ data }) => {
      if (alive) setAuthedUserId(data.user?.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthedUserId(session?.user?.id ?? null);
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const listFn = useServerFn(listCvLibrary);
  const saveFn = useServerFn(saveCvLibraryItem);
  const delFn = useServerFn(deleteCvLibraryItem);
  const fetchItemFn = useServerFn(fetchCvLibraryItem);
  const qc = useQueryClient();

  const libraryQuery = useQuery({
    queryKey: ["cv-library", authedUserId],
    enabled: open && Boolean(authedUserId),
    queryFn: async () => (await listFn()) as {
      items: Array<{
        id: string;
        kind: "cv" | "support";
        original_filename: string;
        mime_type: string;
        size_bytes: number;
        label: string;
        created_at: string;
      }>;
    },
  });

  async function uploadToBucketAndSave(
    file: { name: string; mime: string; size: number; base64: string },
    kind: "cv" | "support",
  ) {
    if (!authedUserId) throw new Error("Sign in to save documents to your library.");
    const bin = atob(file.base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const safe = file.name.replace(/[^a-z0-9._-]+/gi, "_").slice(0, 120);
    const path = `${authedUserId}/${kind}/${crypto.randomUUID()}-${safe}`;
    const { error: upErr } = await supabase.storage
      .from("cv-library")
      .upload(path, new Blob([bytes], { type: file.mime }), {
        contentType: file.mime,
        upsert: false,
      });
    if (upErr) throw new Error(upErr.message);
    await saveFn({
      data: {
        kind,
        storage_path: path,
        original_filename: file.name,
        mime_type: file.mime as any,
        size_bytes: file.size,
        label: "",
      },
    });
  }

  const saveCvMut = useMutation({
    mutationFn: async () => {
      if (!cvBase64Ref.current || !cvFileMeta) {
        throw new Error("Upload a CV first.");
      }
      await uploadToBucketAndSave(
        {
          name: cvFileMeta.name,
          mime: cvBase64Ref.current.mime,
          size: cvFileMeta.size,
          base64: cvBase64Ref.current.base64,
        },
        "cv",
      );
    },
    onSuccess: () => {
      toast.success("CV saved to your library.");
      qc.invalidateQueries({ queryKey: ["cv-library"] });
    },
    onError: (e: any) => toast.error(e?.message || "Could not save CV."),
  });

  const saveSupportMut = useMutation({
    mutationFn: async (idx: number) => {
      const doc = supportDocs[idx];
      if (!doc) throw new Error("Document not found.");
      await uploadToBucketAndSave(doc, "support");
    },
    onSuccess: () => {
      toast.success("Saved to your library.");
      qc.invalidateQueries({ queryKey: ["cv-library"] });
    },
    onError: (e: any) => toast.error(e?.message || "Could not save document."),
  });

  const deleteLibMut = useMutation({
    mutationFn: async (id: string) => {
      await delFn({ data: { id } });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cv-library"] });
    },
    onError: (e: any) => toast.error(e?.message || "Delete failed."),
  });

  /** Schedule a delete with a 6-second undo window; toast action cancels it. */
  function requestDelete(id: string, name: string) {
    // Hide immediately from the list.
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    const cancel = () => {
      const t = deleteTimers.current.get(id);
      if (t) clearTimeout(t);
      deleteTimers.current.delete(id);
      setPendingDeleteIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    };
    const timer = setTimeout(() => {
      deleteTimers.current.delete(id);
      deleteLibMut.mutate(id, {
        onSuccess: () => {
          setPendingDeleteIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          toast.success(`Deleted "${name}".`);
        },
        onError: () => cancel(),
      });
    }, 6000);
    deleteTimers.current.set(id, timer);
    toast(`"${name}" will be deleted in 6s`, {
      duration: 6000,
      action: { label: "Undo", onClick: cancel },
    });
  }

  async function openLibPreview(item: {
    id: string;
    kind: "cv" | "support";
    original_filename: string;
    mime_type: string;
  }) {
    try {
      setLibPreviewLoading(item.id);
      const res = (await fetchItemFn({ data: { id: item.id } })) as {
        base64: string;
        filename: string;
        mime: string;
        size: number;
        kind: "cv" | "support";
      };
      const bin = atob(res.base64);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([u8], { type: res.mime }));
      setLibPreview((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return { id: item.id, name: res.filename, mime: res.mime, url, kind: res.kind };
      });
    } catch (e: any) {
      toast.error(e?.message || "Could not open preview.");
    } finally {
      setLibPreviewLoading(null);
    }
  }

  const useLibItemMut = useMutation({
    mutationFn: async (item: {
      id: string;
      kind: "cv" | "support";
      original_filename: string;
    }) => {
      const res = (await fetchItemFn({ data: { id: item.id } })) as {
        base64: string;
        filename: string;
        mime: string;
        size: number;
        kind: "cv" | "support";
      };
      if (item.kind === "cv") {
        cvBase64Ref.current = { mime: res.mime, base64: res.base64 };
        setCvFileMeta({ name: res.filename, mime: res.mime, size: res.size });
        toast.success(`Loaded "${res.filename}" as your CV.`);
        // Re-run extraction so the bio, name, email, phone, city and skills
        // fields are auto-populated from the stored CV — matches the
        // behaviour of a fresh upload.
        await extractAndFillFromCv({
          name: res.filename,
          mime: res.mime,
          data: res.base64,
        });
      } else {
        setSupportDocs((prev) => {
          if (prev.length >= 10) {
            toast.error("Max 10 supporting documents.");
            return prev;
          }
          if (prev.some((d) => d.name === res.filename && d.size === res.size)) {
            toast.message(`"${res.filename}" is already added.`);
            return prev;
          }
          return [
            ...prev,
            { name: res.filename, mime: res.mime, size: res.size, base64: res.base64 },
          ];
        });
        toast.success(`Added "${res.filename}" as supporting document.`);
      }
    },
    onError: (e: any) => toast.error(e?.message || "Could not load document."),
  });

  // Drag-and-drop handlers for supporting documents.
  function reorderSupport(from: number, to: number) {
    if (from === to || from < 0 || to < 0) return;
    setSupportDocs((prev) => {
      if (from >= prev.length || to >= prev.length) return prev;
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }

  // Structured fields
  const [companyName, setCompanyName] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [hiringManager, setHiringManager] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [keyAchievements, setKeyAchievements] = useState("");
  const [awards, setAwards] = useState("");
  const [certifications, setCertifications] = useState("");
  const [quantifiedWins, setQuantifiedWins] = useState("");
  const [targetVacancy, setTargetVacancy] = useState("");
  // Snapshot of the last tailored CV JSON so we can run the alignment check
  // between the CV's achievements/awards/metrics and the motivational letter.
  const [lastTailoredCv, setLastTailoredCv] = useState<any | null>(null);
  const [skills, setSkills] = useState("");

  // Tailored-CV controls (feature: manual address override, strict 1-page,
  // live overflow warning).
  const [addressOverride, setAddressOverride] = useState("");
  const [addressOverrideTouched, setAddressOverrideTouched] = useState(false);
  const [strictOnePage, setStrictOnePage] = useState(false);
  const [layoutWarning, setLayoutWarning] = useState<null | {
    pageCount: number;
    scale: number;
    strict: boolean;
  }>(null);

  // When the AI detects a job location, auto-fill the override field so the
  // user sees exactly what will be used and can adjust it. Only overwrite the
  // field while the user hasn't manually edited it.
  useEffect(() => {
    const loc = extracted?.location?.trim() || "";
    if (!addressOverrideTouched && loc && loc !== addressOverride) {
      setAddressOverride(loc);
    }
  }, [extracted?.location, addressOverride, addressOverrideTouched]);

  // Keep the speculative jobText brief in sync with the selected vacancy mode
  // — user can flip current/future/both without re-clicking Preload.
  useEffect(() => {
    if (!specContactId) return;
    const c = SPECULATIVE_LEGAL_CONTACTS.find((x) => x.id === specContactId);
    if (!c) return;
    setJobText(buildSpeculativeBrief(c, vacancyMode));
    setValidationOverride(false);
    // We deliberately only re-run when vacancyMode or the contact changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vacancyMode, specContactId]);

  // Selected Apollo contact + issues derived from the current letter.
  const selectedContact =
    SPECULATIVE_LEGAL_CONTACTS.find((x) => x.id === specContactId) ?? null;
  const letterFullTextForCheck = (() => {
    if (!letter) return "";
    const parts = [
      letter.recipient,
      letter.employer,
      letter.salutation,
      letter.body,
      letter.sign_off,
      letter.candidateName,
    ];
    return parts.filter(Boolean).join("\n");
  })();
  const letterIssues: SpeculativeLetterIssue[] = letter
    ? validateSpeculativeLetter(letterFullTextForCheck, selectedContact)
    : [];
  const hasBlockingIssue = letterIssues.some((i) => i.severity === "error");

  // Alignment check: every achievement / award / metric shown on the tailored
  // CV should also appear in the motivational letter for this specific role.
  const alignmentIssues: AlignmentIssue[] = (() => {
    if (!letter) return [];
    // Build the CV metrics source from the tailored CV (if we have one) plus
    // whatever the user typed into the structured fields.
    const cvBullets: string[] = [];
    const cv = lastTailoredCv;
    if (cv?.summary) cvBullets.push(String(cv.summary));
    if (Array.isArray(cv?.experience)) {
      for (const e of cv.experience) {
        if (Array.isArray(e?.bullets)) cvBullets.push(...e.bullets.map(String));
      }
    }
    if (Array.isArray(cv?.achievements)) cvBullets.push(...cv.achievements.map(String));

    const achievements: string[] = [
      ...(Array.isArray(cv?.achievements) ? cv.achievements.map(String) : []),
      ...(keyAchievements
        ? keyAchievements.split(/\n+/).map((s) => s.replace(/^[-•\s]+/, "").trim()).filter(Boolean)
        : []),
    ];
    const awardsList: Array<{ title?: string } | string> = [
      ...(Array.isArray(cv?.awards) ? cv.awards : []),
      ...(awards
        ? awards.split(/\n+/).map((s) => s.replace(/^[-•\s]+/, "").trim()).filter(Boolean)
        : []),
    ];
    const extraKw = quantifiedWins
      ? quantifiedWins.split(/\n+/).map((s) => s.trim()).filter(Boolean)
      : [];

    return checkCvLetterAlignment({
      achievements,
      awards: awardsList,
      cvMetricsSource: cvBullets.join("\n"),
      extraKeywords: extraKw,
      targetVacancy: targetVacancy.trim() || undefined,
      letterText: letterFullTextForCheck,
    });
  })();
  const hasAlignmentBlock = alignmentIssues.some((i) => i.severity === "error");



  function onImagePick(f: File | null) {
    if (f && !(f instanceof File)) {
      // eslint-disable-next-line no-console
      console.warn("[MotivationalLetter] onImagePick got non-File", f);
      return;
    }
    imageFileRef.current = f;
    if (!f) {
      setPreview(null);
      return;
    }
    if (!f.type?.startsWith("image/")) {
      toast.error("Please choose an image file.");
      imageFileRef.current = null;
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8MB).");
      imageFileRef.current = null;
      return;
    }
    try {
      setPreview(URL.createObjectURL(f));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[MotivationalLetter] preview URL failed", e);
    }
  }

  async function onCvPick(f: File | null) {
    if (!f) {
      cvBase64Ref.current = null;
      setCvFileMeta(null);
      return;
    }
    if (!(f instanceof File)) {
      // eslint-disable-next-line no-console
      console.warn("[MotivationalLetter] onCvPick got non-File", f);
      toast.error("Invalid file — please try again.");
      return;
    }
    const allowed = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
    if (!allowed.includes(f.type)) {
      toast.error("CV must be a PDF, PNG or JPG.");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("CV file too large (max 15MB).");
      return;
    }
    let mime = "";
    let data = "";
    try {
      const encoded = await fileToBase64(f);
      mime = encoded?.mime ?? "";
      data = encoded?.data ?? "";
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[MotivationalLetter] fileToBase64 failed", e);
      toast.error("Could not read the file. Please try another.");
      return;
    }
    if (!data) {
      toast.error("Uploaded file is empty.");
      return;
    }
    cvBase64Ref.current = { mime: f.type || mime, base64: data };
    setCvFileMeta({ name: f.name, mime: f.type, size: f.size });

    await extractAndFillFromCv({ name: f.name, mime: f.type, data });
  }

  /**
   * Run cv-extract on the given base64 data and pre-fill the bio + contact
   * fields. Shared by both fresh uploads and "load from library" actions so
   * users never end up with a stored CV attached but empty form fields.
   */
  async function extractAndFillFromCv(file: { name: string; mime: string; data: string }) {
    setCvExtracting(true);
    try {
      const res = await fetchWithAuth("/api/cv-extract", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: "upload",
          file: { name: file.name, mime: file.mime, data: file.data },
        }),
      });
      const json = (await res.json().catch(() => ({}))) as { extracted?: any };
      const ex = json?.extracted;
      if (res.ok && ex && typeof ex === "object") {
        const parts: string[] = [];
        if (typeof ex.summary === "string" && ex.summary) parts.push(ex.summary);
        if (Array.isArray(ex.experience) && ex.experience.length) {
          parts.push(
            "EXPERIENCE:\n" +
              ex.experience
                .map((e: any) => {
                  const role = e?.role ?? "";
                  const company = e?.company ?? "";
                  const start = e?.start ?? "";
                  const end = e?.end ?? "";
                  const bullets = Array.isArray(e?.bullets) ? e.bullets : [];
                  return (
                    `- ${role} @ ${company} (${start}–${end})` +
                    (bullets.length ? `\n  • ${bullets.join("\n  • ")}` : "")
                  );
                })
                .join("\n"),
          );
        }
        if (Array.isArray(ex.education) && ex.education.length) {
          parts.push(
            "EDUCATION:\n" +
              ex.education
                .map(
                  (e: any) =>
                    `- ${e?.qualification ?? ""}, ${e?.institution ?? ""} (${e?.year ?? ""})`,
                )
                .join("\n"),
          );
        }
        if (Array.isArray(ex.skills) && ex.skills.length)
          parts.push("SKILLS: " + ex.skills.join(", "));
        const bg = parts.filter(Boolean).join("\n\n");
        // Always overwrite when we successfully re-read a CV — the user
        // explicitly asked for auto-fill, so stale values from a different
        // CV must be replaced (not merged) to avoid confusion.
        if (bg) setCandidate(bg);
        if (ex.fullName) setCandidateName(String(ex.fullName));
        if (ex.email) setEmail(String(ex.email));
        if (ex.phone) setPhone(String(ex.phone));
        if (ex.location) setCity(String(ex.location));
        if (Array.isArray(ex.skills) && ex.skills.length)
          setSkills(ex.skills.join(", "));
        toast.success("CV read — details pre-filled below.");
        return true;
      }
      toast.message("CV attached — couldn't auto-read text, but it'll still be merged into the PDF.");
      return false;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[MotivationalLetter] cv-extract failed", e);
      toast.message("CV attached — auto-read skipped.");
      return false;
    } finally {
      setCvExtracting(false);
    }
  }

  async function onSupportPick(files: FileList | null) {
    if (!files || files.length === 0) return;
    const MAX_BYTES = 15 * 1024 * 1024;
    const MAX_DOCS = 10;
    const allowedMime = new Set([
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
    ]);
    const allowedExt = /\.(pdf|png|jpe?g)$/i;
    const added: Array<{ name: string; mime: string; size: number; base64: string }> = [];
    const rejected: string[] = [];
    const remainingSlots = () => MAX_DOCS - supportDocs.length - added.length;

    for (const f of Array.from(files)) {
      const name = f.name || "file";
      const mimeOk = f.type && allowedMime.has(f.type.toLowerCase());
      const extOk = allowedExt.test(name);
      if (!mimeOk && !extOk) {
        rejected.push(`${name}: unsupported file type — use PDF, PNG or JPG.`);
        continue;
      }
      if (f.size === 0) {
        rejected.push(`${name}: file is empty (0 bytes).`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        const mb = (f.size / (1024 * 1024)).toFixed(1);
        rejected.push(`${name}: ${mb} MB is too large — max 15 MB per file.`);
        continue;
      }
      if (remainingSlots() <= 0) {
        rejected.push(
          `${name}: skipped — you can only attach up to ${MAX_DOCS} supporting documents.`,
        );
        continue;
      }
      const isDupe = [...supportDocs, ...added].some(
        (d) => d.name === name && d.size === f.size,
      );
      if (isDupe) {
        rejected.push(`${name}: already added.`);
        continue;
      }
      try {
        const { mime, data } = await fileToBase64(f);
        if (!data) {
          rejected.push(`${name}: could not read file contents.`);
          continue;
        }
        added.push({
          name,
          mime: (f.type || mime || "application/octet-stream").toLowerCase(),
          size: f.size,
          base64: data,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[MotivationalLetter] support read failed", name, e);
        rejected.push(`${name}: could not read file (it may be corrupted).`);
      }
    }

    if (added.length) {
      setSupportDocs((prev) => [...prev, ...added]);
      toast.success(
        `Added ${added.length} supporting document${added.length > 1 ? "s" : ""}.`,
      );
    }
    if (rejected.length) {
      // Show one summary toast + the first specific reason so the user knows what to fix.
      const first = rejected[0];
      const extra = rejected.length > 1 ? ` (+${rejected.length - 1} more)` : "";
      toast.error(first + extra, {
        description:
          rejected.length > 1
            ? rejected.slice(1, 4).join("\n") +
              (rejected.length > 4 ? `\n…and ${rejected.length - 4} more` : "")
            : undefined,
        duration: 6000,
      });
    }
    if (supportInputRef.current) supportInputRef.current.value = "";
  }


  function removeSupport(idx: number) {
    setSupportDocs((prev) => prev.filter((_, i) => i !== idx));
  }

  function overridesPayload() {
    return {
      companyName: companyName.trim() || undefined,
      roleTitle: roleTitle.trim() || undefined,
      hiringManager: hiringManager.trim() || undefined,
      candidateName: candidateName.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      city: city.trim() || undefined,
      keyAchievements: keyAchievements.trim() || undefined,
      awards: awards.trim() || undefined,
      certifications: certifications.trim() || undefined,
      quantifiedWins: quantifiedWins.trim() || undefined,
      targetVacancy: targetVacancy.trim() || undefined,
      skills: skills.trim() || undefined,
    };
  }

  const extractMut = useMutation({
    mutationFn: async () => {
      const f = imageFileRef.current;
      if (!f && !jobText.trim())
        throw new Error("Upload a job ad photo or paste the description first.");
      const jobImage = f ? await fileToBase64(f) : null;
      const res = await fetchWithAuth("/api/motivational-letter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "extract", jobText, jobImage }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        extracted?: Extracted;
      };
      if (!res.ok) throw new Error(data?.error || `Failed to read job ad (${res.status}).`);
      const ex = data?.extracted ?? ({} as Extracted);
      return {
        roleTitle: ex?.roleTitle ?? "",
        employer: ex?.employer ?? "",
        location: ex?.location ?? "",
        recipient: ex?.recipient ?? "",
        responsibilities: Array.isArray(ex?.responsibilities) ? ex.responsibilities : [],
        requirements: Array.isArray(ex?.requirements) ? ex.requirements : [],
        closingDate: ex?.closingDate ?? "",
        referenceNumber: ex?.referenceNumber ?? "",
      } satisfies Extracted;
    },
    onSuccess: (ex) => {
      setExtracted(ex);
      if (ex.roleTitle && !roleTitle) setRoleTitle(ex.roleTitle);
      if (ex.employer && !companyName) setCompanyName(ex.employer);
      if (ex.recipient && !hiringManager && ex.recipient !== "Hiring Manager")
        setHiringManager(ex.recipient);
      toast.success("Read job ad — key fields pre-filled below.");
    },
    onError: (e: any) => {
      // eslint-disable-next-line no-console
      console.error("[MotivationalLetter] extract failed", e);
      toast.error(e?.message || "Couldn't read the job ad.");
    },
  });



  const gen = useMutation({
    mutationFn: async () => {
      const info = (candidate ?? "").trim() || candidateInfoProp.trim();
      if (!info && !keyAchievements.trim()) {
        throw new Error("Add your background/CV details or key achievements first.");
      }
      const f = imageFileRef.current;
      if (!f && !jobText.trim() && !roleTitle.trim()) {
        throw new Error("Upload a photo, paste the job description, or enter the role title.");
      }
      const jobImage = f ? await fileToBase64(f) : null;
      const payload = {
        mode: "generate" as const,
        jobText,
        jobImage,
        candidateInfo: info,
        tone,
        overrides: overridesPayload(),
      };
      // eslint-disable-next-line no-console
      console.debug("[MotivationalLetter] generate payload", {
        hasImage: Boolean(jobImage),
        jobTextLen: jobText.length,
        infoLen: info.length,
        roleTitle: payload.overrides.roleTitle,
        companyName: payload.overrides.companyName,
        tone,
      });
      const res = await fetchWithAuth("/api/motivational-letter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; letter?: Letter };
      if (!res.ok) throw new Error(data?.error || `Failed to generate letter (${res.status}).`);
      if (!data?.letter || typeof data.letter !== "object") {
        throw new Error("AI returned an empty letter — please try again.");
      }
      return data.letter;
    },
    onSuccess: (l) => {
      setLetter(l);
      toast.success("Motivational letter ready.");
    },
    onError: (e: any) => {
      // eslint-disable-next-line no-console
      console.error("[MotivationalLetter] generate failed", e);
      toast.error(e?.message || "Couldn't generate letter.");
    },
  });

  // Draft the covering EMAIL that the candidate sends with the application.
  const emailMut = useMutation({
    mutationFn: async () => {
      const info = (candidate ?? "").trim() || candidateInfoProp.trim();
      const f = imageFileRef.current;
      const jobImage = f ? await fileToBase64(f) : null;
      const res = await fetchWithAuth("/api/motivational-letter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "email",
          jobText,
          jobImage,
          candidateInfo: info,
          tone,
          overrides: overridesPayload(),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        email?: {
          subject: string;
          greeting: string;
          body: string;
          sign_off: string;
          signature: string;
        };
      };
      if (!res.ok) throw new Error(data?.error || `Failed to draft email (${res.status}).`);
      if (!data?.email) throw new Error("AI returned an empty email — please try again.");
      return data.email;
    },
    onSuccess: (e) => {
      setAppEmail(e);
      toast.success("Application email drafted.");
    },
    onError: (e: any) => toast.error(e?.message || "Couldn't draft the email."),
  });

  // Generate a JOB-TAILORED CV (as CVData JSON), then download as PDF from the
  // public cv-pdf route. Uses locationOverride from the job ad so the CV
  // address auto-updates to align with the post requirements.
  const tailorCvMut = useMutation({
    mutationFn: async () => {
      const info = (candidate ?? "").trim() || candidateInfoProp.trim();
      if (!info.trim() && !cvBase64Ref.current) {
        throw new Error("Add your background/CV details or upload a CV first.");
      }
      const f = imageFileRef.current;
      const jobImage = f ? await fileToBase64(f) : null;
      // Prefer the user's manual address override (or the AI-detected job
      // location) so the tailored CV address always aligns with the post.
      const locationOverride =
        addressOverride.trim() || extracted?.location?.trim() || "";
      const res = await fetchWithAuth("/api/motivational-letter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "tailor_cv",
          jobText,
          jobImage,
          candidateInfo: info,
          tone,
          overrides: overridesPayload(),
          locationOverride,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; cv?: any };
      if (!res.ok) throw new Error(data?.error || `Failed to tailor CV (${res.status}).`);
      if (!data?.cv) throw new Error("AI returned an empty CV — please try again.");

      // Force the address override on the returned CV (final safeguard —
      // never let the AI override the user's manual choice).
      const cv = data.cv;
      if (locationOverride) cv.location = locationOverride;
      // Snapshot for the alignment check between CV and letter.
      setLastTailoredCv(cv);
      const filename = `${(cv.fullName || candidateName || "CV").replace(/[^a-z0-9_\- ]/gi, "_").replace(/\s+/g, "_")}_${(roleTitle || "Tailored").replace(/[^a-z0-9]+/gi, "_")}_CV.pdf`;
      const pdfRes = await fetch("/api/public/cv-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cv,
          accentHex: "#0B2545",
          filename,
          unbranded: true,
          strictOnePage,
        }),
      });
      if (!pdfRes.ok) {
        const t = await pdfRes.text().catch(() => "");
        throw new Error(t || `Failed to render tailored CV PDF (${pdfRes.status}).`);
      }
      const pageCount = Number(pdfRes.headers.get("X-Cv-Page-Count") || "1") || 1;
      const scale = Number(pdfRes.headers.get("X-Cv-Scale") || "1") || 1;
      const strict = pdfRes.headers.get("X-Cv-Strict") === "1";
      const blob = await pdfRes.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 800);
      return { filename, location: cv.location, pageCount, scale, strict };
    },
    onSuccess: (r) => {
      setLayoutWarning({ pageCount: r.pageCount, scale: r.scale, strict: r.strict });
      const addressNote = r.location ? ` (address: ${r.location})` : "";
      if (r.pageCount > 1) {
        toast.warning(
          `Tailored CV downloaded${addressNote} — content overflowed to page ${r.pageCount}. Enable Strict 1-page mode or shorten details, then regenerate.`,
        );
      } else if (r.strict && r.scale < 0.8) {
        toast.success(
          `Tailored CV downloaded${addressNote} — fit on 1 page at ${Math.round(r.scale * 100)}% scale.`,
        );
      } else {
        toast.success(`Tailored CV downloaded${addressNote}.`);
      }
    },
    onError: (e: any) => toast.error(e?.message || "Couldn't tailor the CV."),
  });

  // Convenience: generate the motivational letter AND the tailored CV in one click.
  const bothMut = useMutation({
    mutationFn: async () => {
      // Kick off both in parallel — the letter will render into <pre>, the
      // tailored CV will download automatically.
      const [_letter] = await Promise.all([gen.mutateAsync(), tailorCvMut.mutateAsync()]);
      return _letter;
    },
    onError: (e: any) => toast.error(e?.message || "Couldn't run both generators."),
  });

  /**
   * Preload the form for a speculative (unsolicited) application to an
   * Apollo.io contact. Sets employer / recipient / role / city and builds a
   * jobText brief that primes the AI to write a professional unsolicited
   * enquiry disclosing Apollo.io as the source of the contact details.
   */
  function preloadSpeculative(c: SpeculativeLegalContact) {
    setCompanyName(c.company);
    setHiringManager(c.name || "");
    setRoleTitle(SPECULATIVE_COMBINED_ROLE);
    // Sync address + city + extracted so the letter heading, CV address and
    // "detected" panel all match the Apollo.io lead's location.
    if (c.location) {
      setAddressOverride(c.location);
      setAddressOverrideTouched(false);
      setCity(c.location);
      setExtracted({
        employer: c.company,
        recipient: c.name || "",
        location: c.location,
        roleTitle: SPECULATIVE_COMBINED_ROLE,
      });
    } else {
      setExtracted(null);
    }
    setJobText(buildSpeculativeBrief(c, vacancyMode));
    // Reset any prior guardrail override on a fresh preload.
    setValidationOverride(false);
    imageFileRef.current = null;
    setPreview(null);
    toast.success(
      c.name
        ? `Preloaded speculative application to ${c.name} at ${c.company}.`
        : `Preloaded speculative application to ${c.company}.`,
    );
  }

  /** Generate letter + tailored CV + application email in one click. */
  const specGenerateAllMut = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled([
        gen.mutateAsync(),
        tailorCvMut.mutateAsync(),
        emailMut.mutateAsync(),
      ]);
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length === results.length) {
        throw new Error("All generators failed — please try again.");
      }
      return failed.length;
    },
    onSuccess: (failedCount) => {
      if (failedCount === 0) {
        toast.success("Speculative pack ready: letter, tailored CV and email.");
      } else {
        toast.warning(`Pack partially ready — ${failedCount} step(s) failed. See individual errors above.`);
      }
    },
    onError: (e: any) =>
      toast.error(e?.message || "Couldn't generate the speculative pack."),
  });




  const pdfFn = useServerFn(generateLetterPdf);

  /** Filesystem-safe token for filenames inside the ZIP. */
  function slug(s: string, fallback = "file"): string {
    const t = (s || "").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
    return t || fallback;
  }

  /**
   * Generate letter + tailored CV + email for a single role and return the
   * raw bytes/text. Used by the ZIP packagers below. Reuses all form state
   * (candidate info, job text, tone, overrides, address override, strict mode).
   */
  async function generateRolePack(role: string): Promise<{
    role: string;
    letterPdf: Uint8Array;
    cvPdf: Uint8Array;
    emailText: string;
    candidateName: string;
    letter: Letter;
  }> {
    const info = (candidate ?? "").trim() || candidateInfoProp.trim();
    if (!info && !keyAchievements.trim()) {
      throw new Error("Add your background/CV details or key achievements first.");
    }
    const f = imageFileRef.current;
    const jobImage = f ? await fileToBase64(f) : null;
    const base = overridesPayload();
    const overrides = { ...base, roleTitle: role || base.roleTitle };
    const locationOverride =
      addressOverride.trim() || extracted?.location?.trim() || "";

    async function callMode<T>(mode: "generate" | "email" | "tailor_cv"): Promise<T> {
      const res = await fetchWithAuth("/api/motivational-letter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode,
          jobText,
          jobImage,
          candidateInfo: info,
          tone,
          overrides,
          ...(mode === "tailor_cv" ? { locationOverride } : {}),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as any;
      if (!res.ok) throw new Error(data?.error || `Failed (${res.status}) for ${mode}.`);
      return data as T;
    }

    const [letterData, emailData, cvData] = await Promise.all([
      callMode<{ letter: Letter }>("generate"),
      callMode<{ email: { subject: string; greeting: string; body: string; sign_off: string; signature: string } }>("email"),
      callMode<{ cv: any }>("tailor_cv"),
    ]);
    const letterObj = letterData.letter;
    const email = emailData.email;
    const cv = cvData.cv;
    if (locationOverride) cv.location = locationOverride;

    // Render CV PDF via public route (accepts raw CV JSON).
    const cvPdfRes = await fetch("/api/public/cv-pdf", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        cv,
        accentHex: "#0B2545",
        filename: `${slug(cv.fullName || candidateName || "CV")}_${slug(role, "Role")}_CV.pdf`,
        unbranded: true,
        strictOnePage,
      }),
    });
    if (!cvPdfRes.ok) {
      const t = await cvPdfRes.text().catch(() => "");
      throw new Error(t || `Failed to render tailored CV PDF (${cvPdfRes.status}).`);
    }
    const cvPdfBytes = new Uint8Array(await cvPdfRes.arrayBuffer());

    // Render letter PDF via server fn.
    const letterFilename = `${slug(letterObj.candidateName || "Motivational_Letter")}_${slug(role, "Role")}_Letter.pdf`;
    const letterRes = (await pdfFn({
      data: {
        letter: letterObj,
        filename: letterFilename,
        accentHex: "#0B2545",
        cvAttachment: null,
        attachments: [],
      },
    })) as { base64?: string } | undefined;
    if (!letterRes?.base64) throw new Error("Server did not return the letter PDF.");
    const bin = atob(letterRes.base64);
    const letterBytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) letterBytes[i] = bin.charCodeAt(i);

    const emailText = `Subject: ${email.subject}\n\n${email.greeting}\n\n${email.body}\n\n${email.sign_off}\n${email.signature}`;

    return {
      role,
      letterPdf: letterBytes,
      cvPdf: cvPdfBytes,
      emailText,
      candidateName: letterObj.candidateName || candidateName || "Candidate",
      letter: letterObj,
    };
  }

  /** Trigger a browser download for a Blob. */
  function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 800);
  }

  /**
   * Multi-role packager. Generates one folder per selected role (letter PDF +
   * tailored CV PDF + email .txt) and downloads a single ZIP. If no roles are
   * selected, uses the current roleTitle field as a single-pack fallback.
   */
  const packMut = useMutation({
    mutationFn: async () => {
      const roles = selectedRoles.length
        ? selectedRoles.slice()
        : [roleTitle.trim() || "Application"];
      const zip = new JSZip();
      const contact = SPECULATIVE_LEGAL_CONTACTS.find((x) => x.id === specContactId);
      const contactLabel = contact
        ? `${slug(contact.name || contact.company)}`
        : slug(companyName || "Application");
      const errors: string[] = [];
      let candidateForName = candidateName || "Candidate";

      for (const role of roles) {
        try {
          const pack = await generateRolePack(role);
          candidateForName = pack.candidateName;
          const folder = zip.folder(slug(role, "Role"))!;
          folder.file(`${slug(pack.candidateName)}_${slug(role)}_Letter.pdf`, pack.letterPdf);
          folder.file(`${slug(pack.candidateName)}_${slug(role)}_CV.pdf`, pack.cvPdf);
          const emailHeader = contact
            ? `To: ${contact.name ? contact.name + " <" + contact.businessEmail + ">" : contact.businessEmail}\nCompany: ${contact.company}${contact.location ? "\nLocation: " + contact.location : ""}\n\n`
            : "";
          folder.file(`${slug(pack.candidateName)}_${slug(role)}_Email.txt`, emailHeader + pack.emailText);
        } catch (e: any) {
          errors.push(`${role}: ${e?.message || "failed"}`);
        }
      }
      if (errors.length === roles.length) {
        throw new Error(errors.join("\n"));
      }
      if (errors.length) {
        zip.file("ERRORS.txt", errors.join("\n"));
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const zipName = `${slug(candidateForName)}_${contactLabel}_Applications.zip`;
      downloadBlob(blob, zipName);
      return { roleCount: roles.length - errors.length, errorCount: errors.length, zipName };
    },
    onSuccess: (r) => {
      if (r.errorCount === 0) {
        toast.success(`ZIP ready: ${r.roleCount} role pack${r.roleCount > 1 ? "s" : ""} — "${r.zipName}"`);
      } else {
        toast.warning(`ZIP downloaded — ${r.roleCount} succeeded, ${r.errorCount} failed. See ERRORS.txt inside.`);
      }
    },
    onError: (e: any) => toast.error(e?.message || "Couldn't build the ZIP package."),
  });

  /**
   * Zip the CURRENTLY generated letter + email + a freshly rendered tailored
   * CV for the current single role (uses form state). No AI regeneration for
   * the letter/email — reuses what the user already reviewed on-screen.
   */
  const currentZipMut = useMutation({
    mutationFn: async () => {
      if (!letter) throw new Error("Generate the letter first.");
      if (!appEmail) throw new Error("Draft the application email first.");

      const zip = new JSZip();
      const nameSlug = slug(letter.candidateName || candidateName || "Candidate");
      const roleSlug = slug(letter.roleTitle || roleTitle || "Role");

      // Letter PDF (reuse the same server fn — no AI re-run).
      const letterRes = (await pdfFn({
        data: {
          letter,
          filename: `${nameSlug}_${roleSlug}_Letter.pdf`,
          accentHex: "#0B2545",
          cvAttachment: null,
          attachments: [],
        },
      })) as { base64?: string } | undefined;
      if (!letterRes?.base64) throw new Error("Server did not return the letter PDF.");
      const lbin = atob(letterRes.base64);
      const lbytes = new Uint8Array(lbin.length);
      for (let i = 0; i < lbin.length; i++) lbytes[i] = lbin.charCodeAt(i);
      zip.file(`${nameSlug}_${roleSlug}_Letter.pdf`, lbytes);

      // Freshly tailor + render a CV for this exact role (so the CV in the ZIP
      // is job-specific, not the uploaded raw CV).
      try {
        const pack = await generateRolePack(letter.roleTitle || roleTitle || "Application");
        zip.file(`${nameSlug}_${roleSlug}_CV.pdf`, pack.cvPdf);
      } catch (e: any) {
        zip.file("CV_ERROR.txt", `Could not render tailored CV: ${e?.message || "unknown error"}`);
      }

      const contact = SPECULATIVE_LEGAL_CONTACTS.find((x) => x.id === specContactId);
      const emailHeader = contact
        ? `To: ${contact.name ? contact.name + " <" + contact.businessEmail + ">" : contact.businessEmail}\nCompany: ${contact.company}${contact.location ? "\nLocation: " + contact.location : ""}\n\n`
        : "";
      const emailText = `Subject: ${appEmail.subject}\n\n${appEmail.greeting}\n\n${appEmail.body}\n\n${appEmail.sign_off}\n${appEmail.signature}`;
      zip.file(`${nameSlug}_${roleSlug}_Email.txt`, emailHeader + emailText);

      const blob = await zip.generateAsync({ type: "blob" });
      const zipName = `${nameSlug}_${roleSlug}_Pack.zip`;
      downloadBlob(blob, zipName);
      return zipName;
    },
    onSuccess: (name) => toast.success(`Downloaded "${name}".`),
    onError: (e: any) => toast.error(e?.message || "Couldn't build the ZIP."),
  });

  /** Convert a base64 string to Uint8Array. */
  function b64ToBytes(base64: string): Uint8Array {
    const bin = atob(base64);
    const u8 = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
    return u8;
  }
  /** Convert Uint8Array back to base64 (chunked for large payloads). */
  function bytesToB64(bytes: Uint8Array): string {
    let bin = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)) as any);
    }
    return btoa(bin);
  }

  /**
   * Generate a single combined PDF (motivational letter followed by the
   * tailored CV) for a given role. Works with a pasted CV — no upload
   * required. Downloads the merged PDF directly.
   */
  const combinedPdfMut = useMutation({
    mutationFn: async (role?: string) => {
      const targetRole = (role || roleTitle || "Application").trim();
      const pack = await generateRolePack(targetRole);
      // Merge letter + CV into one PDF using the existing server fn's
      // ability to append a PDF attachment.
      const letterFilename = `${slug(pack.candidateName)}_${slug(targetRole)}_Letter_and_CV.pdf`;
      const cvB64 = bytesToB64(pack.cvPdf);
      const supportAttachments = supportDocs.map((d) => ({
        mime: d.mime,
        base64: d.base64,
        name: d.name,
      }));
      const res = (await pdfFn({
        data: {
          letter: pack.letter,
          filename: letterFilename,
          accentHex: "#0B2545",
          cvAttachment: { mime: "application/pdf", base64: cvB64 },
          attachments: supportAttachments,
        },
      })) as { base64?: string; filename?: string } | undefined;
      if (!res?.base64) throw new Error("Server did not return the combined PDF.");
      const bytes = b64ToBytes(res.base64);
      const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
      downloadBlob(blob, res.filename || letterFilename);
      return { role: targetRole, filename: res.filename || letterFilename };
    },
    onSuccess: (r) => toast.success(`Downloaded "${r.filename}".`),
    onError: (e: any) => toast.error(e?.message || "Couldn't generate combined PDF."),
  });

  /**
   * Multi-role combined-PDF packager. For each selected role, generates a
   * single combined Letter + tailored CV PDF and triggers a download.
   * Replaces the previous ZIP flow entirely.
   */
  const multiCombinedMut = useMutation({
    mutationFn: async () => {
      const roles = selectedRoles.length
        ? selectedRoles.slice()
        : [roleTitle.trim() || "Application"];
      const errors: string[] = [];
      let ok = 0;
      for (const role of roles) {
        try {
          const pack = await generateRolePack(role);
          const cvB64 = bytesToB64(pack.cvPdf);
          const filename = `${slug(pack.candidateName)}_${slug(role)}_Letter_and_CV.pdf`;
          const res = (await pdfFn({
            data: {
              letter: pack.letter,
              filename,
              accentHex: "#0B2545",
              cvAttachment: { mime: "application/pdf", base64: cvB64 },
              attachments: supportDocs.map((d) => ({ mime: d.mime, base64: d.base64, name: d.name })),
            },
          })) as { base64?: string; filename?: string } | undefined;
          if (!res?.base64) throw new Error("Empty PDF from server.");
          downloadBlob(
            new Blob([b64ToBytes(res.base64) as BlobPart], { type: "application/pdf" }),
            res.filename || filename,
          );
          ok++;
          // Small delay so the browser doesn't collapse concurrent downloads.
          await new Promise((r) => setTimeout(r, 400));
        } catch (e: any) {
          errors.push(`${role}: ${e?.message || "failed"}`);
        }
      }
      if (ok === 0) throw new Error(errors.join("\n") || "All roles failed.");
      return { ok, failed: errors.length };
    },
    onSuccess: (r) => {
      if (r.failed === 0) {
        toast.success(`Downloaded ${r.ok} combined PDF${r.ok > 1 ? "s" : ""} (letter + CV per role).`);
      } else {
        toast.warning(`Downloaded ${r.ok} PDFs, ${r.failed} failed.`);
      }
    },
    onError: (e: any) => toast.error(e?.message || "Couldn't generate combined PDFs."),
  });
  const pdfMut = useMutation({
    mutationFn: async (opts: { merge: boolean; includeSupport?: boolean }) => {
      if (!letter || typeof letter !== "object") {
        throw new Error("Generate the letter first.");
      }
      if (opts.merge && !cvBase64Ref.current) {
        throw new Error("Upload your CV first to merge it with the letter.");
      }
      if (opts.includeSupport && supportDocs.length === 0) {
        throw new Error("Upload at least one supporting document first.");
      }
      const parts = [
        opts.merge ? "with_CV" : null,
        opts.includeSupport ? "with_Supporting_Docs" : null,
      ].filter(Boolean);
      const suffix = parts.length ? `_${parts.join("_")}` : "";
      const safeName = (letter.candidateName || "Motivational_Letter").replace(/\s+/g, "_");
      const safeRole = (letter.roleTitle || "Role").replace(/[^a-z0-9]+/gi, "_");
      const filename = `${safeName}_${safeRole}${suffix}.pdf`;
      // eslint-disable-next-line no-console
      console.debug("[MotivationalLetter] pdf request", {
        merge: opts.merge,
        includeSupport: Boolean(opts.includeSupport),
        supportCount: supportDocs.length,
        hasCv: Boolean(cvBase64Ref.current),
        filename,
      });
      const res = (await pdfFn({
        data: {
          letter,
          filename,
          accentHex: "#0B2545",
          cvAttachment: opts.merge ? cvBase64Ref.current : null,
          attachments: opts.includeSupport
            ? supportDocs.map((d) => ({ mime: d.mime, base64: d.base64, name: d.name }))
            : [],
        },
      })) as { base64?: string; filename?: string; contentType?: string } | undefined;
      if (!res || typeof res.base64 !== "string") {
        throw new Error("Server did not return a PDF. Please try again.");
      }
      const bin = atob(res.base64);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      const blob = new Blob([u8], { type: res.contentType || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename || filename;

      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 800);
      toast.success(`Saved "${res.filename || filename}".`);
    },
    onError: (e: any, variables) => {
      // eslint-disable-next-line no-console
      console.error("[MotivationalLetter] pdf failed", e);
      const isComplete = Boolean(variables?.includeSupport);
      const raw = String(e?.message || "").trim();
      const friendly = /413|too large|payload/i.test(raw)
        ? "Your files are too large to merge in one go. Remove one document and try again."
        : /network|fetch|failed to fetch|load failed/i.test(raw)
        ? "Network hiccup — check your connection and try again."
        : /401|unauth/i.test(raw)
        ? "Your session expired. Sign in again to download the PDF."
        : raw || "PDF generation failed.";
      toast.error(
        isComplete
          ? `Complete package failed: ${friendly}`
          : `PDF download failed: ${friendly}`,
        { duration: 7000 },
      );
    },

  });

  function formattedLetter(l: Letter | null | undefined): string {
    if (!l || typeof l !== "object") return "";
    const role = l.roleTitle ?? "";
    const employer = l.employer ?? "";
    // Strip a leading "RE:" line from the AI body so it doesn't duplicate
    // the RE line we add ourselves.
    let bodyText = l.body ?? "";
    bodyText = bodyText.replace(/^\s*RE\s*:[^\n]*\n+/i, "");
    return [
      l.candidateName ?? "",
      l.candidateContact ?? "",
      "",
      l.date ?? "",
      "",
      // Mention the recipient exactly ONCE — via the salutation below.
      employer,
      "",
      l.salutation ?? "",
      "",
      `RE: Application for ${role}${employer ? ` at ${employer}` : ""}`,
      "",
      bodyText,
      "",
      l.sign_off ?? "",
      "",
      l.candidateName ?? "",
    ].join("\n");
  }



  function copyLetter() {
    if (!letter) return;
    navigator.clipboard.writeText(formattedLetter(letter));
    toast.success("Copied to clipboard.");
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o && !candidate && candidateInfoProp) setCandidate(candidateInfoProp);
      }}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="border-gold/60 text-navy hover:bg-gold/10"
        >
          <FileText className="mr-2 h-4 w-4" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">
            <Sparkles className="mr-2 inline h-4 w-4 text-gold" />
            AI Motivational Letter Generator
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* ---- SPECULATIVE / APOLLO.IO LEADS ---- */}
          <section className="rounded-lg border border-gold/40 bg-gold/5 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-medium uppercase tracking-wider text-navy">
                <Sparkles className="mr-1.5 inline h-3.5 w-3.5 text-gold" />
                Speculative applications (Apollo.io leads)
              </p>
              <span className="text-[11px] text-foreground/60">
                {SPECULATIVE_LEGAL_CONTACTS.length} preloaded contacts
              </span>
            </div>
            <p className="text-[11px] leading-relaxed text-foreground/70">
              Send a highly professional <strong>unsolicited</strong> enquiry for
              Articles of Clerkship, Candidate Attorney, Paralegal, Legal Assistant
              or Internship. The letter and email will politely disclose that the
              recipient's details were obtained from <em>app.apollo.io</em>.
            </p>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
              <select
                value={specContactId}
                onChange={(e) => setSpecContactId(e.target.value)}
                className="h-9 rounded-md border border-navy/30 bg-background px-2 text-xs text-foreground"
                aria-label="Select an Apollo.io contact to preload"
              >
                <option value="">— Select a preloaded contact —</option>
                {SPECULATIVE_LEGAL_CONTACTS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {(c.name || "—") +
                      (c.position ? `, ${c.position}` : "") +
                      ` · ${c.company}` +
                      (c.location ? ` · ${c.location}` : "")}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={!specContactId}
                onClick={() => {
                  const c = SPECULATIVE_LEGAL_CONTACTS.find(
                    (x) => x.id === specContactId,
                  );
                  if (c) preloadSpeculative(c);
                }}
              >
                <FileText className="mr-1.5 h-3.5 w-3.5" /> Preload
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={
                  !specContactId ||
                  specGenerateAllMut.isPending ||
                  gen.isPending ||
                  tailorCvMut.isPending ||
                  emailMut.isPending
                }
                onClick={() => {
                  const c = SPECULATIVE_LEGAL_CONTACTS.find(
                    (x) => x.id === specContactId,
                  );
                  if (!c) return;
                  preloadSpeculative(c);
                  // Kick off letter + tailored CV + email in one go.
                  setTimeout(() => specGenerateAllMut.mutate(), 0);
                }}
                className="bg-navy text-navy-foreground hover:bg-navy/90 disabled:opacity-50"
              >
                {specGenerateAllMut.isPending ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                )}
                Preload &amp; generate letter + CV + email
              </Button>
            </div>

            {/* Vacancy focus toggle — rewrites the ask between currently
                available openings, future vacancies, or both (default). */}
            <div className="rounded-md border border-navy/20 bg-background/60 p-3 space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-navy">
                Vacancy focus (letter wording)
              </p>
              <div className="flex flex-wrap gap-2">
                {([
                  { v: "both", label: "Current & future (recommended)" },
                  { v: "current", label: "Currently available openings only" },
                  { v: "future", label: "Future vacancies only" },
                ] as Array<{ v: VacancyMode; label: string }>).map((o) => (
                  <label
                    key={o.v}
                    className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition ${
                      vacancyMode === o.v
                        ? "border-navy bg-navy text-navy-foreground"
                        : "border-navy/30 bg-background text-navy hover:bg-navy/10"
                    }`}
                  >
                    <input
                      type="radio"
                      name="vacancy-mode"
                      value={o.v}
                      className="h-3 w-3 accent-gold"
                      checked={vacancyMode === o.v}
                      onChange={() => setVacancyMode(o.v)}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
              <p className="text-[11px] leading-relaxed text-foreground/60">
                All three modes still produce <strong>one</strong> letter addressed to the
                same specific person and firm — only the wording of the ask changes.
              </p>
            </div>

            {/* Multi-role package selector */}
            <div className="rounded-md border border-navy/20 bg-background/60 p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-navy">
                  <Package className="mr-1 inline h-3 w-3" />
                  Multi-role application package
                </p>
                <span className="text-[10px] text-foreground/60">
                  {selectedRoles.length
                    ? `${selectedRoles.length} role${selectedRoles.length > 1 ? "s" : ""} selected`
                    : "None selected — uses current role title"}
                </span>
              </div>
              <p className="text-[11px] leading-relaxed text-foreground/70">
                Tick one or more positions to generate a <strong>single combined PDF
                (motivational letter + tailored CV)</strong> per role — one download per position.
              </p>
              <div className="flex flex-wrap gap-2">
                {ROLE_OPTIONS.map((r) => {
                  const checked = selectedRoles.includes(r);
                  return (
                    <label
                      key={r}
                      className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition ${
                        checked
                          ? "border-navy bg-navy text-navy-foreground"
                          : "border-navy/30 bg-background text-navy hover:bg-navy/10"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="h-3 w-3 accent-gold"
                        checked={checked}
                        onChange={(e) => {
                          setSelectedRoles((prev) =>
                            e.target.checked
                              ? [...prev, r]
                              : prev.filter((x) => x !== r),
                          );
                        }}
                      />
                      {r}
                    </label>
                  );
                })}
                {selectedRoles.length > 0 && (
                  <button
                    type="button"
                    className="text-[11px] text-navy/70 underline"
                    onClick={() => setSelectedRoles([])}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  type="button"
                  size="sm"
                  disabled={multiCombinedMut.isPending}
                  onClick={() => multiCombinedMut.mutate()}
                  className="bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50"
                  title="Generate one combined Letter + tailored CV PDF per selected role"
                >
                  {multiCombinedMut.isPending ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FileDown className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Generate combined PDF{selectedRoles.length > 1 ? `s (${selectedRoles.length} roles)` : ""}
                </Button>
                {selectedRoles.length > 0 && (
                  <button
                    type="button"
                    className="text-[11px] text-navy/70 underline self-center"
                    onClick={() =>
                      setSelectedRoles(ROLE_OPTIONS.map((r) => r))
                    }
                  >
                    Select all {ROLE_OPTIONS.length}
                  </button>
                )}
                {selectedRoles.length === 0 && (
                  <button
                    type="button"
                    className="text-[11px] text-navy/70 underline self-center"
                    onClick={() =>
                      setSelectedRoles(ROLE_OPTIONS.map((r) => r))
                    }
                  >
                    Select all {ROLE_OPTIONS.length} positions
                  </button>
                )}
              </div>
            </div>
            {specContactId && (() => {
              const c = SPECULATIVE_LEGAL_CONTACTS.find((x) => x.id === specContactId);
              if (!c) return null;
              return (
                <div className="rounded-md border border-navy/20 bg-white/60 p-3 text-[11px] leading-relaxed text-foreground/80">
                  <div>
                    <strong className="text-navy">To:</strong>{" "}
                    {c.name || "—"}
                    {c.position ? ` (${c.position})` : ""} — {c.company}
                  </div>
                  <div>
                    <strong className="text-navy">Email:</strong> {c.businessEmail}
                    {c.personalEmail ? ` · Personal: ${c.personalEmail}` : ""}
                  </div>
                  {c.location && (
                    <div>
                      <strong className="text-navy">Location:</strong> {c.location}
                    </div>
                  )}
                  <div className="mt-1 text-foreground/60">
                    Add your CV / background in step 3 first for the strongest tailored letter.
                  </div>
                </div>
              );
            })()}
          </section>

          {/* ---- STEP 1: JOB SOURCE ---- */}

          <section className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
            <p className="text-xs font-medium uppercase tracking-wider text-navy">
              1. The job you're applying for
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
              >
                <ImagePlus className="mr-2 h-4 w-4" />
                {preview ? "Replace photo" : "Upload job ad photo"}
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => onImagePick(e.target.files?.[0] ?? null)}
              />
              {preview && (
                <div className="relative">
                  <img
                    src={preview}
                    alt="Job ad preview"
                    className="h-20 w-auto rounded-md border border-border object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      onImagePick(null);
                      if (fileRef.current) fileRef.current.value = "";
                    }}
                    className="absolute -right-2 -top-2 rounded-full bg-white p-0.5 shadow"
                    aria-label="Remove image"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => extractMut.mutate()}
                disabled={extractMut.isPending || (!imageFileRef.current && !jobText.trim())}
              >
                {extractMut.isPending ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ScanText className="mr-1.5 h-3.5 w-3.5" />
                )}
                Read job ad
              </Button>
            </div>

            <Textarea
              value={jobText}
              onChange={(e) => setJobText(e.target.value)}
              placeholder="Or paste the job ad / requirements here…"
              className="min-h-[90px] text-sm"
            />

            {extracted && (extracted.requirements?.length || extracted.responsibilities?.length) ? (
              <div className="rounded-md border border-navy/20 bg-white/60 p-3 text-xs">
                {extracted.location && (
                  <p className="text-foreground/60">Location: {extracted.location}</p>
                )}
                {extracted.requirements?.length ? (
                  <div className="mt-1">
                    <p className="font-medium text-navy">Requirements detected:</p>
                    <ul className="ml-4 list-disc text-foreground/80">
                      {extracted.requirements.slice(0, 8).map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>

          {/* ---- STEP 2: KEY DETAILS ---- */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-3">
            <p className="text-xs font-medium uppercase tracking-wider text-navy">
              2. Key details (auto-filled from photo where possible)
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Company / employer</Label>
                <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="mt-1 h-9" />
              </div>
              <div>
                <Label className="text-xs">Role / job title</Label>
                <Input value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} className="mt-1 h-9" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Hiring manager (optional)</Label>
                <Input
                  value={hiringManager}
                  onChange={(e) => setHiringManager(e.target.value)}
                  placeholder="e.g. Ms Naledi Khumalo"
                  className="mt-1 h-9"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-navy/40 text-navy hover:bg-navy/5"
                onClick={() => {
                  setCandidateName(ADMIN_PROFILE.candidateName);
                  setEmail(ADMIN_PROFILE.email);
                  setPhone(ADMIN_PROFILE.phone);
                  toast.success("Filled in Nkadimeng Danny's contact details.");
                }}
                title="Auto-fill name, email and phone for Nkadimeng Danny"
              >
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                Use Nkadimeng Danny details
              </Button>
              <span className="text-[11px] text-foreground/60">
                Applies name, email &amp; phone across the letter, CV and email.
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label className="text-xs">Your full name</Label>
                <Input value={candidateName} onChange={(e) => setCandidateName(e.target.value)} className="mt-1 h-9" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-9" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 h-9" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">City / location</Label>
                <Input value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 h-9" />
              </div>
            </div>

            {/* Role-specific tailoring: rewrites Experience, Achievements
                and Awards on the CV — and the letter — for THIS vacancy. */}
            <div className="rounded-md border border-navy/20 bg-navy/5 p-3">
              <Label className="text-xs font-semibold text-navy">
                Target vacancy (role-specific tailoring)
              </Label>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <select
                  value={targetVacancy}
                  onChange={(e) => setTargetVacancy(e.target.value)}
                  className="h-9 min-w-[220px] flex-1 rounded-md border border-input bg-background px-2 text-sm"
                >
                  <option value="">— None (generic) —</option>
                  <option value="Internship">Internship</option>
                  <option value="Candidate Attorney">Candidate Attorney</option>
                  <option value="Articles of Clerkship">Articles of Clerkship</option>
                  <option value="Paralegal">Paralegal</option>
                  <option value="Legal Assistant">Legal Assistant</option>
                  <option value="Compliance Officer">Compliance Officer</option>
                  <option value="Legal Intern (Vacation Work)">Legal Intern (Vacation Work)</option>
                </select>
                <Input
                  value={targetVacancy}
                  onChange={(e) => setTargetVacancy(e.target.value)}
                  placeholder="Or type a custom vacancy…"
                  className="h-9 min-w-[220px] flex-1"
                />
              </div>
              <p className="mt-1.5 text-[11px] leading-relaxed text-foreground/70">
                When set, the AI rewrites your <strong>Experience</strong>,{" "}
                <strong>Key Achievements</strong> and <strong>Awards</strong> to
                match this specific vacancy, and names it explicitly in the
                motivational letter.
              </p>
            </div>

            <div className="pt-2">
              <Label className="text-xs">Key achievements (one per line — appear at the top of the CV)</Label>
              <Textarea
                value={keyAchievements}
                onChange={(e) => setKeyAchievements(e.target.value)}
                placeholder={"e.g.\n- Rolled out POPIA framework and trained 120 staff\n- Cut audit findings by 40% in one year"}
                className="mt-1 min-h-[90px] text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Quantified wins (one per line — numbers, %, R-values, timeframes)</Label>
              <Textarea
                value={quantifiedWins}
                onChange={(e) => setQuantifiedWins(e.target.value)}
                placeholder={"e.g.\n- Drafted 30+ contracts with zero disputes in 12 months\n- Reduced case backlog by 45% (R2.1m recovered)"}
                className="mt-1 min-h-[80px] text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Awards & honours (one per line: Title — Issuer (Year))</Label>
              <Textarea
                value={awards}
                onChange={(e) => setAwards(e.target.value)}
                placeholder={"e.g.\n- Dean's Merit List — UNISA (2023)\n- Best Moot Speaker — UNISA Moot Court (2022)"}
                className="mt-1 min-h-[70px] text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Certifications & CPDs (one per line)</Label>
              <Textarea
                value={certifications}
                onChange={(e) => setCertifications(e.target.value)}
                placeholder={"e.g.\n- POPIA & PAIA Certification — SAIBPP (2024)\n- Legal Drafting Short Course — UP (2023)"}
                className="mt-1 min-h-[70px] text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Skills to highlight (comma-separated)</Label>
              <Input
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
                placeholder="POPIA, FICA, risk assessment, MS 365"
                className="mt-1 h-9"
              />
            </div>
          </section>

          {/* ---- STEP 3: CV BACKGROUND ---- */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-3">
            <p className="text-xs font-medium uppercase tracking-wider text-navy">
              3. Your CV / background
            </p>

            <div className="flex flex-wrap items-center gap-3 rounded-md border border-dashed border-navy/30 bg-navy/5 p-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => cvFileInputRef.current?.click()}
                disabled={cvExtracting}
              >
                {cvExtracting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="mr-2 h-4 w-4" />
                )}
                {cvFileMeta ? "Replace CV" : "Upload your CV (PDF or image)"}
              </Button>
              <input
                ref={cvFileInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                className="hidden"
                onChange={(e) => onCvPick(e.target.files?.[0] ?? null)}
              />
              {cvFileMeta && (
                <div className="flex items-center gap-2 text-xs text-foreground/70">
                  <span className="truncate max-w-[220px]">{cvFileMeta.name}</span>
                  <span className="text-foreground/40">
                    ({Math.round(cvFileMeta.size / 1024)} KB)
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      onCvPick(null);
                      if (cvFileInputRef.current) cvFileInputRef.current.value = "";
                    }}
                    className="rounded-full bg-white p-0.5 shadow"
                    aria-label="Remove CV"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {cvFileMeta && authedUserId && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => saveCvMut.mutate()}
                  disabled={saveCvMut.isPending}
                >
                  {saveCvMut.isPending ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Save className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Save CV to library
                </Button>
              )}
              <p className="basis-full text-[11px] text-foreground/60">
                We'll read your CV to tailor the letter, then let you download a single
                <strong> Letter + CV PDF</strong>.
                {authedUserId ? " Save it to your library to reuse next time." : ""}
              </p>
            </div>

            <Textarea
              value={candidate}
              onChange={(e) => setCandidate(e.target.value)}
              placeholder="Paste your CV or bio (auto-filled if you upload one above)…"
              className="min-h-[120px] text-sm"
            />
            <p className="text-[11px] text-foreground/60">
              Pre-filled from your CV Builder inputs or uploaded CV. Edit before generating.
            </p>
          </section>

          {/* ---- STEP 4: SUPPORTING DOCUMENTS ---- */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-3">
            <p className="text-xs font-medium uppercase tracking-wider text-navy">
              4. Supporting documents (certificates, ID, references)
            </p>
            <p className="text-[11px] text-foreground/60">
              Add PDFs or images (PNG/JPG). They'll be merged after your letter and CV into one
              ready-to-send PDF. Max 10 files · 15MB each.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => supportInputRef.current?.click()}
                disabled={supportDocs.length >= 10}
              >
                <FileText className="mr-2 h-4 w-4" />
                Add supporting document{supportDocs.length ? "s" : ""}
              </Button>
              <input
                ref={supportInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                multiple
                className="hidden"
                onChange={(e) => onSupportPick(e.target.files)}
              />
              {supportDocs.length > 0 && (
                <span className="text-xs text-foreground/60">
                  {supportDocs.length} attached
                </span>
              )}
            </div>

            {supportDocs.length > 0 && (
              <>
                <p className="text-[11px] text-foreground/60">
                  Drag to reorder — attachments are merged in this exact sequence after the letter and CV.
                </p>
                <ul className="divide-y divide-border rounded-md border border-border">
                  {supportDocs.map((d, i) => {
                    const isDragging = dragIndex === i;
                    const isOver = dragOverIndex === i && dragIndex !== null && dragIndex !== i;
                    return (
                      <li
                        key={`${d.name}-${i}`}
                        draggable
                        onDragStart={(e) => {
                          setDragIndex(i);
                          e.dataTransfer.effectAllowed = "move";
                          try {
                            e.dataTransfer.setData("text/plain", String(i));
                          } catch {
                            /* ignore */
                          }
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                          if (dragOverIndex !== i) setDragOverIndex(i);
                        }}
                        onDragLeave={() => {
                          if (dragOverIndex === i) setDragOverIndex(null);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragIndex !== null) reorderSupport(dragIndex, i);
                          setDragIndex(null);
                          setDragOverIndex(null);
                        }}
                        onDragEnd={() => {
                          setDragIndex(null);
                          setDragOverIndex(null);
                        }}
                        className={`flex items-center justify-between gap-2 px-3 py-2 text-xs transition-colors ${
                          isDragging ? "opacity-40" : ""
                        } ${isOver ? "bg-navy/5 ring-1 ring-inset ring-navy/30" : ""}`}
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <GripVertical
                            className="h-3.5 w-3.5 shrink-0 cursor-grab text-foreground/40"
                            aria-label="Drag to reorder"
                          />
                          <span className="w-4 shrink-0 text-center font-mono text-[10px] text-foreground/50">
                            {i + 1}
                          </span>
                          <FileText className="h-3.5 w-3.5 text-navy shrink-0" />
                          <span className="truncate">{d.name}</span>
                          <span className="text-foreground/40 shrink-0">
                            ({Math.round(d.size / 1024)} KB)
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => reorderSupport(i, i - 1)}
                            disabled={i === 0}
                            className="rounded-full bg-white p-1 shadow disabled:opacity-30"
                            aria-label={`Move ${d.name} up`}
                            title="Move up"
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => reorderSupport(i, i + 1)}
                            disabled={i === supportDocs.length - 1}
                            className="rounded-full bg-white p-1 shadow disabled:opacity-30"
                            aria-label={`Move ${d.name} down`}
                            title="Move down"
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                          {authedUserId && (
                            <button
                              type="button"
                              onClick={() => saveSupportMut.mutate(i)}
                              disabled={saveSupportMut.isPending}
                              className="rounded-full bg-white p-1 shadow hover:bg-navy/10 disabled:opacity-50"
                              aria-label={`Save ${d.name} to library`}
                              title="Save to library"
                            >
                              <Save className="h-3.5 w-3.5 text-navy" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removeSupport(i)}
                            className="rounded-full bg-white p-1 shadow"
                            aria-label={`Remove ${d.name}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </section>

          {/* ---- MY LIBRARY ---- */}
          {authedUserId && (() => {
            const allItems = libraryQuery.data?.items ?? [];
            const visibleItems = allItems.filter((it) => !pendingDeleteIds.has(it.id));
            const totalBytes = visibleItems.reduce((s, it) => s + (it.size_bytes || 0), 0);
            const cvCount = visibleItems.filter((it) => it.kind === "cv").length;
            const supCount = visibleItems.filter((it) => it.kind === "support").length;
            const SOFT_LIMIT_BYTES = 100 * 1024 * 1024; // 100 MB soft cap for display
            const usedPct = Math.min(100, Math.round((totalBytes / SOFT_LIMIT_BYTES) * 100));
            const fmtSize = (b: number) =>
              b >= 1024 * 1024
                ? `${(b / (1024 * 1024)).toFixed(1)} MB`
                : `${Math.max(1, Math.round(b / 1024))} KB`;
            const isPreviewable = (mime: string) =>
              mime === "application/pdf" || mime.startsWith("image/");
            return (
              <section className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium uppercase tracking-wider text-navy">
                    <FolderOpen className="mr-1.5 inline h-3.5 w-3.5" />
                    My saved library
                  </p>
                  {libraryQuery.isFetching && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-navy/60" />
                  )}
                </div>

                {/* Storage usage panel */}
                <div className="rounded-md border border-border bg-card p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-2 text-navy">
                      <HardDrive className="h-3.5 w-3.5" />
                      <span className="font-medium">Storage used</span>
                    </div>
                    <span className="text-foreground/70">
                      {fmtSize(totalBytes)} of {fmtSize(SOFT_LIMIT_BYTES)} · {usedPct}%
                    </span>
                  </div>
                  <div
                    className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-navy/10"
                    role="progressbar"
                    aria-valuenow={usedPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className={`h-full transition-all ${
                        usedPct > 85 ? "bg-red-500" : usedPct > 60 ? "bg-amber-500" : "bg-navy"
                      }`}
                      style={{ width: `${usedPct}%` }}
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-foreground/60">
                    <span>Total files: <strong className="text-navy">{visibleItems.length}</strong></span>
                    <span>CVs: <strong className="text-navy">{cvCount}</strong></span>
                    <span>Supporting: <strong className="text-navy">{supCount}</strong></span>
                  </div>
                </div>

                <p className="text-[11px] text-foreground/60">
                  Your uploaded CV and supporting documents are stored securely on our server so
                  you can reuse them the next time you generate a motivational letter. Preview
                  a file before loading it back into this session.
                </p>
                {visibleItems.length ? (
                  <ul className="divide-y divide-border rounded-md border border-border bg-card">
                    {visibleItems.map((item) => (
                      <li
                        key={item.id}
                        className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <FileText className="h-3.5 w-3.5 text-navy shrink-0" />
                          <span className="truncate" title={item.original_filename}>
                            {item.original_filename}
                          </span>
                          <span
                            className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${
                              item.kind === "cv"
                                ? "bg-navy/10 text-navy"
                                : "bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            {item.kind === "cv" ? "CV" : "Supporting"}
                          </span>
                          <span className="text-foreground/40 shrink-0">
                            ({fmtSize(item.size_bytes)})
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {isPreviewable(item.mime_type) && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => openLibPreview(item)}
                              disabled={libPreviewLoading === item.id}
                              title="Preview file"
                            >
                              {libPreviewLoading === item.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Eye className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              useLibItemMut.mutate({
                                id: item.id,
                                kind: item.kind,
                                original_filename: item.original_filename,
                              })
                            }
                            disabled={useLibItemMut.isPending}
                          >
                            {useLibItemMut.isPending ? (
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            ) : null}
                            Use
                          </Button>
                          <button
                            type="button"
                            onClick={() => requestDelete(item.id, item.original_filename)}
                            className="rounded-full bg-white p-1 shadow text-red-600 hover:bg-red-50"
                            aria-label={`Delete ${item.original_filename}`}
                            title="Remove from library (6s undo)"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[11px] text-foreground/50 italic">
                    {libraryQuery.isLoading
                      ? "Loading your library…"
                    : "Nothing saved yet. Upload a CV or supporting document above and click the save icon."}
                </p>
              )}
              </section>
            );
          })()}


          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[180px]">
              <Label className="text-xs">Tone</Label>
              <Input value={tone} onChange={(e) => setTone(e.target.value)} className="mt-1 h-9" />
            </div>
            <Button
              onClick={() => gen.mutate()}
              disabled={gen.isPending || bothMut.isPending}
              className="bg-navy text-navy-foreground hover:bg-navy/90"
            >
              {gen.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Writing…
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate letter
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => tailorCvMut.mutate()}
              disabled={tailorCvMut.isPending || bothMut.isPending}
              className="border-navy/40 text-navy hover:bg-navy/5"
              title={
                extracted?.location
                  ? `Tailored CV will use address: ${extracted.location}`
                  : "Rewrites your CV to align with the job requirements"
              }
            >
              {tailorCvMut.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="mr-2 h-4 w-4" />
              )}
              Tailored CV (1-page PDF)
            </Button>
            <Button
              type="button"
              onClick={() => bothMut.mutate()}
              disabled={gen.isPending || tailorCvMut.isPending || bothMut.isPending}
              className="bg-emerald-700 text-white hover:bg-emerald-800"
              title="Generate the motivational letter AND a job-tailored CV in one click"
            >
              {bothMut.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Letter + Tailored CV
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => emailMut.mutate()}
              disabled={emailMut.isPending}
              className="border-gold/60 text-navy hover:bg-gold/10"
              title="Draft the covering email you send with your CV + letter"
            >
              {emailMut.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-2 h-4 w-4" />
              )}
              Draft application email
            </Button>
          </div>
          {/* Tailored-CV controls: manual address override + strict 1-page + overflow warning */}
          <div className="rounded-lg border border-navy/15 bg-navy/5 p-3 space-y-3">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
              <div>
                <Label htmlFor="tailor-address" className="text-xs uppercase tracking-wider text-navy">
                  Tailored CV address
                </Label>
                <Input
                  id="tailor-address"
                  list="sa-cities"
                  value={addressOverride}
                  onChange={(e) => {
                    setAddressOverride(e.target.value);
                    setAddressOverrideTouched(true);
                  }}
                  placeholder={extracted?.location || "e.g. Pretoria, Gauteng"}
                  className="mt-1"
                />
                <p className="mt-1 text-[11px] text-foreground/60">
                  {extracted?.location ? (
                    <>
                      Detected from job ad:{" "}
                      <button
                        type="button"
                        className="text-navy underline"
                        onClick={() => {
                          setAddressOverride(extracted.location || "");
                          setAddressOverrideTouched(false);
                        }}
                      >
                        {extracted.location}
                      </button>
                      . Edit above to override.
                    </>
                  ) : (
                    <>Leave blank to keep the CV's existing address, or type the city/town from the post.</>
                  )}
                </p>
                <datalist id="sa-cities">
                  {[
                    "Johannesburg, Gauteng",
                    "Pretoria, Gauteng",
                    "Sandton, Gauteng",
                    "Midrand, Gauteng",
                    "Centurion, Gauteng",
                    "Cape Town, Western Cape",
                    "Stellenbosch, Western Cape",
                    "Paarl, Western Cape",
                    "George, Western Cape",
                    "Durban, KwaZulu-Natal",
                    "Pietermaritzburg, KwaZulu-Natal",
                    "Richards Bay, KwaZulu-Natal",
                    "Gqeberha, Eastern Cape",
                    "East London, Eastern Cape",
                    "Mthatha, Eastern Cape",
                    "Bloemfontein, Free State",
                    "Welkom, Free State",
                    "Polokwane, Limpopo",
                    "Tzaneen, Limpopo",
                    "Thohoyandou, Limpopo",
                    "Mokopane, Limpopo",
                    "Nelspruit / Mbombela, Mpumalanga",
                    "Witbank / eMalahleni, Mpumalanga",
                    "Secunda, Mpumalanga",
                    "Rustenburg, North West",
                    "Mahikeng, North West",
                    "Kimberley, Northern Cape",
                    "Upington, Northern Cape",
                  ].map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
              <label
                htmlFor="strict-one-page"
                className="flex cursor-pointer items-start gap-2 rounded-md border border-navy/20 bg-background p-3 text-xs"
                title="Force the CV onto exactly one A4 page by shrinking font and spacing further."
              >
                <input
                  id="strict-one-page"
                  type="checkbox"
                  checked={strictOnePage}
                  onChange={(e) => setStrictOnePage(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-navy"
                />
                <span>
                  <span className="block font-semibold text-navy">Strict 1-page mode</span>
                  <span className="block text-foreground/60">
                    Always exactly one A4 page (smaller font/spacing).
                  </span>
                </span>
              </label>
            </div>

            {layoutWarning && (
              <div
                className={
                  layoutWarning.pageCount > 1
                    ? "rounded-md border border-amber-400 bg-amber-50 p-3 text-xs text-amber-900"
                    : layoutWarning.scale < 0.8
                      ? "rounded-md border border-emerald-400 bg-emerald-50 p-3 text-xs text-emerald-900"
                      : "rounded-md border border-emerald-300 bg-emerald-50/60 p-3 text-xs text-emerald-800"
                }
                role="status"
              >
                {layoutWarning.pageCount > 1 ? (
                  <>
                    <strong>Layout warning:</strong> content overflowed onto page {layoutWarning.pageCount}.
                    Turn on <em>Strict 1-page mode</em> above (or trim your Key achievements / Summary)
                    and regenerate to keep everything on one A4 page.
                  </>
                ) : layoutWarning.strict ? (
                  <>
                    ✓ Fit on 1 A4 page in strict mode at {Math.round(layoutWarning.scale * 100)}% scale.
                    {layoutWarning.scale < 0.75 && (
                      <> Consider trimming a few bullets for a more readable result.</>
                    )}
                  </>
                ) : (
                  <>✓ Fit cleanly on 1 A4 page at {Math.round(layoutWarning.scale * 100)}% scale.</>
                )}
              </div>
            )}
          </div>


          {appEmail && (
            <div className="space-y-3 rounded-lg border border-gold/40 bg-gold/5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-medium uppercase tracking-wider text-navy">
                  Application email — copy &amp; paste when sending your attachments
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const full = `Subject: ${appEmail.subject}\n\n${appEmail.greeting}\n\n${appEmail.body}\n\n${appEmail.sign_off}\n${appEmail.signature}`;
                    navigator.clipboard.writeText(full);
                    toast.success("Email copied to clipboard.");
                  }}
                >
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy email
                </Button>
              </div>
              <div className="rounded-md border border-border bg-background/60 p-3 text-sm">
                <p className="mb-2">
                  <span className="text-[11px] uppercase tracking-wide text-foreground/50">Subject:</span>{" "}
                  <strong className="text-navy">{appEmail.subject}</strong>
                </p>
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
{`${appEmail.greeting}\n\n${appEmail.body}\n\n${appEmail.sign_off}\n${appEmail.signature}`}
                </pre>
              </div>
              <p className="text-[11px] text-foreground/50">
                Attach your CV and motivational letter to the email before sending.
              </p>
            </div>
          )}

          {letter && (
            <div className="space-y-3 rounded-lg border border-border bg-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs text-foreground/60">
                  Role: <strong className="text-navy">{letter.roleTitle}</strong>
                  {letter.employer ? (
                    <>
                      {" "}
                      · Employer: <strong className="text-navy">{letter.employer}</strong>
                    </>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={copyLetter}>
                    <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => pdfMut.mutate({ merge: false })}
                    disabled={pdfMut.isPending || (hasBlockingIssue && !validationOverride)}
                    className="bg-gold text-gold-foreground hover:bg-gold/90 disabled:opacity-50"
                    title={
                      hasBlockingIssue && !validationOverride
                        ? "Fix the guardrail issues above or tick 'Download anyway' first."
                        : undefined
                    }
                  >
                    {pdfMut.isPending && !cvBase64Ref.current ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <FileDown className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Letter PDF
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => pdfMut.mutate({ merge: true })}
                    disabled={
                      pdfMut.isPending ||
                      !cvBase64Ref.current ||
                      (hasBlockingIssue && !validationOverride)
                    }
                    className="bg-navy text-navy-foreground hover:bg-navy/90 disabled:opacity-50"
                    title={
                      hasBlockingIssue && !validationOverride
                        ? "Fix the guardrail issues above or tick 'Download anyway' first."
                        : cvBase64Ref.current
                          ? "Download letter merged with your CV"
                          : "Upload your CV to enable"
                    }
                  >
                    {pdfMut.isPending && cvBase64Ref.current ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <FileDown className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Letter + CV PDF
                  </Button>
                  <Button
                    size="sm"
                    onClick={() =>
                      pdfMut.mutate({
                        merge: Boolean(cvBase64Ref.current),
                        includeSupport: supportDocs.length > 0,
                      })
                    }
                    disabled={
                      pdfMut.isPending ||
                      (!cvBase64Ref.current && supportDocs.length === 0) ||
                      (hasBlockingIssue && !validationOverride)
                    }
                    aria-busy={
                      pdfMut.isPending && Boolean(pdfMut.variables?.includeSupport)
                    }
                    className="bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60 disabled:cursor-not-allowed"
                    title={
                      hasBlockingIssue && !validationOverride
                        ? "Fix the guardrail issues above or tick 'Download anyway' first."
                        : cvBase64Ref.current || supportDocs.length
                          ? "Download letter + CV + supporting documents as one ready-to-send PDF"
                          : "Add your CV or a supporting document to enable"
                    }
                  >
                    {pdfMut.isPending && pdfMut.variables?.includeSupport ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <FileDown className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    {pdfMut.isPending && pdfMut.variables?.includeSupport
                      ? "Preparing package…"
                      : "Complete package"}
                    {!(pdfMut.isPending && pdfMut.variables?.includeSupport) && (
                      <>
                        {cvBase64Ref.current ? " · CV" : ""}
                        {supportDocs.length ? ` · +${supportDocs.length}` : ""}
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={hasBlockingIssue && !validationOverride}
                    title={
                      hasBlockingIssue && !validationOverride
                        ? "Fix the guardrail issues above or tick 'Download anyway' first."
                        : undefined
                    }
                    onClick={() => {
                      const blob = new Blob([formattedLetter(letter)], {
                        type: "text/plain;charset=utf-8",
                      });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${(letter.candidateName || "Motivational_Letter").replace(
                        /\s+/g,
                        "_",
                      )}.txt`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      setTimeout(() => URL.revokeObjectURL(url), 500);
                    }}
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" /> .txt
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => combinedPdfMut.mutate(letter?.roleTitle || roleTitle)}
                    disabled={
                      combinedPdfMut.isPending ||
                      !letter ||
                      (hasBlockingIssue && !validationOverride)
                    }
                    className="bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50"
                    title={
                      hasBlockingIssue && !validationOverride
                        ? "Fix the guardrail issues above or tick 'Download anyway' first."
                        : supportDocs.length
                          ? `Generate a single combined PDF: motivational letter, tailored CV, and ${supportDocs.length} supporting document${supportDocs.length > 1 ? "s" : ""} (no upload required)`
                          : "Generate a single combined PDF: motivational letter followed by the tailored CV (no upload required)"
                    }
                  >
                    {combinedPdfMut.isPending ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <FileDown className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Letter + CV{supportDocs.length ? ` + ${supportDocs.length} doc${supportDocs.length > 1 ? "s" : ""}` : ""} (combined PDF)
                  </Button>
                </div>
              </div>

              {/* Guardrail: live preview warnings for missing recipient / firm
                  / apology or generic-template phrases. Blocks downloads until
                  the user regenerates or explicitly overrides. */}
              {letterIssues.length > 0 && (
                <div
                  className={
                    hasBlockingIssue
                      ? "rounded-md border-2 border-red-500 bg-red-50 p-3 text-xs text-red-900"
                      : "rounded-md border border-amber-400 bg-amber-50 p-3 text-xs text-amber-900"
                  }
                  role="alert"
                  aria-live="polite"
                >
                  <p className="mb-2 font-semibold">
                    {hasBlockingIssue ? "⛔ Letter guardrail blocked download" : "⚠ Letter guardrail warnings"}
                    {selectedContact ? ` — ${selectedContact.name || selectedContact.company}` : ""}
                  </p>
                  <ul className="ml-4 list-disc space-y-1">
                    {letterIssues.map((iss, i) => (
                      <li key={i}>
                        <strong className="uppercase tracking-wide text-[10px]">
                          {iss.severity === "error" ? "Error" : "Warning"}:
                        </strong>{" "}
                        {iss.message}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => gen.mutate()}
                      disabled={gen.isPending}
                      className="border-red-400 text-red-900 hover:bg-red-100"
                    >
                      {gen.isPending ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      Regenerate letter
                    </Button>
                    {hasBlockingIssue && (
                      <label className="flex cursor-pointer items-center gap-1.5 text-[11px]">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 accent-red-700"
                          checked={validationOverride}
                          onChange={(e) => setValidationOverride(e.target.checked)}
                        />
                        Download anyway (I accept the letter as-is)
                      </label>
                    )}
                  </div>
                </div>
              )}

              {/* CV ↔ Letter alignment check: warns if achievements, awards or
                  metrics in the CV are missing from the motivational letter,
                  and blocks if the letter doesn't name the target vacancy. */}
              {letter && alignmentIssues.length > 0 && (
                <div
                  className={
                    hasAlignmentBlock
                      ? "rounded-md border-2 border-red-500 bg-red-50 p-3 text-xs text-red-900"
                      : "rounded-md border border-amber-400 bg-amber-50 p-3 text-xs text-amber-900"
                  }
                  role="alert"
                  aria-live="polite"
                >
                  <p className="mb-2 font-semibold">
                    {hasAlignmentBlock
                      ? "⛔ CV ↔ Letter alignment failed"
                      : "⚠ CV ↔ Letter alignment warnings"}
                    {targetVacancy ? ` — target: ${targetVacancy}` : ""}
                  </p>
                  <ul className="ml-4 list-disc space-y-1">
                    {alignmentIssues.map((iss, i) => (
                      <li key={i}>
                        <strong className="uppercase tracking-wide text-[10px]">
                          {iss.severity === "error" ? "Error" : "Warning"}:
                        </strong>{" "}
                        {iss.message}
                        {iss.detail ? (
                          <span className="ml-1 text-foreground/70">— {iss.detail}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-[11px] text-foreground/70">
                    Regenerate the letter (or the tailored CV) so both documents
                    tell the same story for this specific application.
                  </p>
                </div>
              )}

              {pdfMut.isPending && pdfMut.variables?.includeSupport && (
                <div
                  className="rounded-md border border-emerald-200 bg-emerald-50/70 p-3"
                  role="status"
                  aria-live="polite"
                >
                  <div className="flex items-center gap-2 text-xs font-medium text-emerald-900">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Merging your letter{cvBase64Ref.current ? ", CV" : ""}
                    {supportDocs.length
                      ? ` and ${supportDocs.length} supporting document${
                          supportDocs.length > 1 ? "s" : ""
                        }`
                      : ""}
                    …
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-emerald-200/70">
                    <div className="h-full w-full animate-pulse rounded-full bg-gradient-to-r from-emerald-500 via-emerald-700 to-emerald-500 bg-[length:200%_100%]" />
                  </div>
                  <p className="mt-1.5 text-[11px] text-emerald-900/70">
                    Large PDFs and images can take up to a minute. Please don't close this window.
                  </p>
                </div>
              )}
              <pre className="max-h-[400px] overflow-y-auto whitespace-pre-wrap rounded-md bg-background/60 p-4 font-sans text-sm leading-relaxed text-foreground">
                {formattedLetter(letter)}
              </pre>
              <p className="text-[11px] text-foreground/50">
                Review and personalise before sending. Verify all facts, dates and names.
              </p>
            </div>
          )}
        </div>
      </DialogContent>

      {/* In-app library file preview */}
      <Dialog
        open={Boolean(libPreview)}
        onOpenChange={(o) => {
          if (!o) {
            if (libPreview?.url) URL.revokeObjectURL(libPreview.url);
            setLibPreview(null);
          }
        }}
      >
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-display text-navy text-sm">
              <FileText className="mr-1.5 inline h-4 w-4" />
              {libPreview?.name}
            </DialogTitle>
          </DialogHeader>
          {libPreview && (
            <div className="space-y-3">
              <p className="text-[11px] text-foreground/60">
                {libPreview.kind === "cv" ? "CV" : "Supporting document"} · {libPreview.mime}
              </p>
              <div className="h-[70vh] w-full overflow-hidden rounded-md border border-border bg-muted/20">
                {libPreview.mime.startsWith("image/") ? (
                  <img
                    src={libPreview.url}
                    alt={libPreview.name}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <iframe
                    src={libPreview.url}
                    title={libPreview.name}
                    className="h-full w-full"
                  />
                )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (libPreview?.url) URL.revokeObjectURL(libPreview.url);
                    setLibPreview(null);
                  }}
                >
                  Close
                </Button>
                <Button
                  size="sm"
                  className="bg-navy text-navy-foreground hover:bg-navy/90"
                  onClick={() => {
                    const item = libPreview;
                    if (!item) return;
                    useLibItemMut.mutate({
                      id: item.id,
                      kind: item.kind,
                      original_filename: item.name,
                    });
                    URL.revokeObjectURL(item.url);
                    setLibPreview(null);
                  }}
                >
                  Load into session
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
